import CampaignsEditModal from "../../components/campaignsEditModal/CampaignsEditModal";
import CampaignsMenu from "../../components/campaignsMenu/CampaignsMenu";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Loading from "../../components/loading/Loading";
import Pagination from "../../components/pagination/Pagination";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";
import { getCampaignsService } from "services/campaign/services";

/* ─── Status badge ───────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    ACTIVE: {
      dot: "bg-green-500 dark:bg-green-400",
      pill: "bg-green-50 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-700/40",
    },
    PAUSED: {
      dot: "bg-yellow-500 dark:bg-yellow-400",
      pill: "bg-yellow-50 text-yellow-700 ring-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-700/40",
    },
  };
  const style = map[status] ?? {
    dot: "bg-red-500 dark:bg-red-400",
    pill: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-700/40",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ring-1 ${style.pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
};

/* ─── Empty state ────────────────────────────────────────────────────── */
const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
      <svg
        width="32"
        height="32"
        fill="none"
        viewBox="0 0 24 24"
        stroke="#1a56db"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
        />
      </svg>
    </div>
    <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">
      No campaigns yet
    </p>
    <p className="text-sm text-gray-400 dark:text-gray-500 max-w-xs">
      {message}
    </p>
  </div>
);

/* ─── Type pill (inline) ─────────────────────────────────────────────── */
const TypeChip = ({ name }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700/40 capitalize">
    {name?.toLowerCase()}
  </span>
);

/* ─── Main component ─────────────────────────────────────────────────── */
const Campaigns = () => {
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const [campaignsData, setCampaignsData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const { translate } = useTranslator();
  const [search] = useState("");

  const getCampaigns = React.useCallback(async () => {
    setIsLoading(true);
    if (!company?._id) {
      setIsLoading(false);
      return;
    }

    console.log("user", user);
    console.log("company", company);

    const data = await getCampaignsService({
      page,
      search,
      companyId: company?._id,
      pageSize: 5,
    });

    setIsLoading(false);
    setCampaignsData(data?.campaigns ?? []);
    const count = data?.totalCampaigns ?? 0;
    setTotalCampaigns(count);
    setPageNumber(Math.ceil(count / 5));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?._id, page, search, userProfile?.user]);

  useEffect(() => {
    getCampaigns();
  }, [getCampaigns]);

  if (userLoading || companyLoading) return null;

  console.log("campaingsData", campaignsData);

  const columns = [
    translate("name"),
    translate("description"),
    translate("type"),
    translate("status"),
    translate("date-programing"),
    translate("actions"),
  ];

  return (
    <CampaignsMenu>
      <div className="max-w-6xl mx-auto">
        {/* ── Page header ── */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-1 h-10 rounded-full bg-blue-700 dark:bg-blue-500" />
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-700 dark:text-blue-400 mb-0.5">
              {translate("campaigns")}
            </p>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {translate("campaigns")}
            </h2>
          </div>
          {totalCampaigns > 0 && (
            <span className="ml-1 px-2.5 py-0.5 rounded-full bg-blue-700 dark:bg-blue-600 text-white text-xs font-bold">
              {totalCampaigns}
            </span>
          )}
        </div>

        {/* ── Empty / Loading state ── */}
        {isLoading && (
          <div className="py-20">
            <Loading />
          </div>
        )}

        {!isLoading && campaignsData.length === 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/60 overflow-hidden">
            <EmptyState message={translate("no-campaigns-found")} />
          </div>
        )}

        {/* ── Table card ── */}
        {campaignsData.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/60 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-700 dark:bg-blue-800">
                  {columns.map((col, i) => (
                    <th
                      key={col}
                      className={`px-4 py-3.5 text-xs font-semibold text-blue-100 uppercase tracking-wider
                        ${i === columns.length - 1 ? "text-center" : "text-left"}`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {campaignsData.map((campaign) => (
                  <tr
                    key={campaign._id}
                    className="group hover:bg-blue-50/40 dark:hover:bg-blue-900/20 transition-colors duration-150"
                  >
                    {/* Name */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="w-0.5 h-5 rounded-full bg-blue-700 dark:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity -ml-1" />
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                          {campaign.name}
                        </span>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-3.5 max-w-[200px]">
                      <span className="text-sm text-gray-500 dark:text-gray-400 truncate block">
                        {campaign.description}
                      </span>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3.5">
                      <TypeChip name={campaign.campaignType?.name} />
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3.5">
                      <StatusBadge status={campaign.status} />
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {new Date(campaign.date).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {new Date(campaign.date).toLocaleTimeString()}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3.5 text-center">
                      <CampaignsEditModal campaign={campaign} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Footer with pagination */}
            {!isLoading && (
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40">
                <Pagination
                  key="paginationCampaigns"
                  data={campaignsData}
                  page={page}
                  pageNumber={pageNumber}
                  isLoading={isLoading}
                  setPage={setPage}
                  totalRecords={totalCampaigns}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </CampaignsMenu>
  );
};

export default Campaigns;
